/**
 * TodoDialog.jsx: make a to-do or edit one. Used by the To Do page (Details, New,
 * a row, ?todo= links) and by Voice Notes ("Make to-do", prefilled).
 *
 *  - Create: POST with an id made when the dialog opens, so a retry or a second
 *    click saves one row. `initial` prefills it ({ title, notes, assigneeId,
 *    source, sourceId, sourceItem }).
 *  - Edit: PUT only the fields changed since the dialog opened, with `was` = the
 *    values they started from; the server refuses (409 todo_changed) a save over
 *    a field someone else saved meanwhile. The form follows the to-do as the page
 *    refetches it: a field you haven't touched takes the new value; one you
 *    have shows the new value with "Use the new version" or "Keep mine", and Save
 *    waits for that choice.
 *  - Fields the server would refuse (canEdit false: an assigned to-do seen by its
 *    non-admin assignee) are read-only with one line saying why. Notes and the
 *    $ mark ("Money") stay editable: anyone who can tick a to-do can mark it.
 *    The mark is saved with the rest; like done, it is never sent in `was`. The
 *    save waits for a row's $ still saving (useUpdateTodo withMoney), so it lands last.
 *  - Only admins pick who a to-do is for. A save that leaves someone else's to-do
 *    linked to a record they can't open (a new assignee or a new link) still goes
 *    through, with a toast saying they'll see its name only.
 *  - onSaved(res) runs after a successful save, before onClose.
 *  - `loading` (edit only): open with a spinner and a close button while the caller
 *    fetches the to-do; the form mounts once it arrives.
 *
 * It shows to-do and record text, so every control has a static data-track
 * (todos.dialog.*) and the dialog is its own "todos" track area.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  TextField, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMobile } from '../../hooks/useMobile';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC } from '../../hooks/useRBAC';
import { useToast } from '../layout/Toast';
import { useCreateTodo, useUpdateTodo, useDeleteTodo, newTodoId } from '../../hooks/useTodos';
import { fmtDue } from '../../lib/todoText';
import AssigneePicker from './AssigneePicker';
import RecordLinkPicker from './RecordLinkPicker';
import TodoLinkChip from './TodoLinkChip';
import MoneyMark from './MoneyMark';

const TITLE_MAX = 300;
const NOTES_MAX = 5000;

const btn = { textTransform: 'none', minHeight: 44 };

function validDay(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const d = new Date(Date.UTC(y, Number(m[2]) - 1, Number(m[3])));
  return y >= 2000 && y <= 2100 && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

const linkKey = (l) => (l?.type ? `${l.type}:${l.id}` : '');

// money is a boolean, so it never shows as a conflict: if both sides changed it, they agree.
const FIELDS = ['title', 'notes', 'dueDate', 'assigneeId', 'link', 'money'];
const same = (k, a, b) => (k === 'link' ? linkKey(a) === linkKey(b) : a === b);
// Body shape of a form value, for the PUT and its `was`.
const toBody = (k, v) => {
  if (k === 'link') return v ? { type: v.type, id: v.id } : null;
  if (k === 'dueDate' || k === 'assigneeId') return v || null;
  return v;
};

function toForm(todo, initial, meId) {
  if (todo) {
    return {
      title: todo.title || '',
      notes: todo.notes || '',
      dueDate: todo.dueDate || '',
      assigneeId: todo.assignee?.id || '',
      link: todo.link?.type ? { type: todo.link.type, id: todo.link.id, label: todo.link.label, sublabel: todo.link.sublabel } : null,
      money: !!todo.money,
    };
  }
  return {
    title: String(initial?.title || '').slice(0, TITLE_MAX),
    notes: String(initial?.notes || '').slice(0, NOTES_MAX),
    dueDate: initial?.dueDate || '',
    // An explicit '' (Team view, Everyone) leaves For empty for the admin to pick.
    assigneeId: initial && Object.hasOwn(initial, 'assigneeId') ? initial.assigneeId || '' : meId || '',
    link: initial?.link || null,
    money: !!initial?.money,
  };
}

/** One line of the conflict box: the value saved meanwhile. */
function newValueText(k, latest, todo, today) {
  switch (k) {
    case 'title': return `New title: ${latest.title}`;
    case 'notes': return latest.notes ? `New notes: ${latest.notes}` : 'The notes were cleared.';
    case 'dueDate': return `New due date: ${latest.dueDate ? fmtDue(latest.dueDate, today) : 'none'}`;
    case 'assigneeId': return `Now for: ${todo.assignee?.name || 'someone else'}`;
    case 'link': return `New link: ${todo.link?.label || 'none'}`;
    default: return '';
  }
}

export default function TodoDialog({ open, loading = false, todo, initial, today, teamView = false, onClose, onSaved, onDeleted }) {
  const { isMobile } = useMobile();
  const create = useCreateTodo();
  const update = useUpdateTodo({ withMoney: true });
  const del = useDeleteTodo();
  const busy = create.isPending || update.isPending || del.isPending;
  // Keep showing what was open while the dialog fades out (the caller clears it on close).
  const shown = useRef({ todo, initial, loading });
  if (open) shown.current = { todo, initial, loading };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      data-track-area="todos"
    >
      {shown.current.loading ? (
        <>
          <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
            <Box component="span" sx={{ flex: 1 }}>To-do</Box>
            <IconButton data-track="todos.dialog.close" onClick={onClose} aria-label="Close" sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} aria-label="Loading" />
          </DialogContent>
        </>
      ) : (
        // Mounted only while open, so each open starts from the to-do (or prefill) as it is now.
        <TodoForm
          key={shown.current.todo?.id || 'new'}
          todo={shown.current.todo}
          initial={shown.current.initial}
          today={today}
          teamView={teamView}
          busy={busy}
          create={create}
          update={update}
          del={del}
          onClose={onClose}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </Dialog>
  );
}

function TodoForm({ todo, initial, today, teamView, busy, create, update, del, onClose, onSaved, onDeleted }) {
  const { isMobile } = useMobile();
  const { profile } = useAuth();
  const { isAdmin } = useRBAC();
  const { toast } = useToast();
  const meId = profile?.id;
  const isEdit = !!todo;
  const editable = !isEdit || todo.canEdit !== false;
  // base = the to-do as this form last took it from the server; f = what is in the fields.
  const [form, setForm] = useState(() => {
    const base = toForm(todo, initial, meId);
    return { base, f: base };
  });
  const { base, f } = form;
  const setF = (fn) => setForm((prev) => ({ ...prev, f: fn(prev.f) }));
  const [error, setError] = useState('');

  // The to-do as the page has it now (it refetches while this is open).
  const latest = useMemo(() => (todo ? toForm(todo) : null), [todo]);
  useEffect(() => {
    if (!latest) return;
    // A field nobody here touched (or edited to the same value) takes the new value.
    setForm((prev) => {
      let next = prev;
      for (const k of FIELDS) {
        if (same(k, latest[k], prev.base[k])) continue;
        if (same(k, prev.f[k], prev.base[k]) || same(k, prev.f[k], latest[k])) {
          next = { base: { ...next.base, [k]: latest[k] }, f: { ...next.f, [k]: latest[k] } };
        }
      }
      return next;
    });
  }, [latest]);
  // Both changed it, differently: the user picks.
  const conflicts = latest
    ? FIELDS.filter((k) => !same(k, latest[k], base[k]) && !same(k, f[k], base[k]) && !same(k, f[k], latest[k]))
    : [];
  const resolve = (useNew) => {
    setError('');
    setForm((prev) => {
      const next = { base: { ...prev.base }, f: { ...prev.f } };
      for (const k of conflicts) {
        next.base[k] = latest[k];
        if (useNew) next.f[k] = latest[k];
      }
      return next;
    });
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  // One id per open: a retried or double-clicked save makes one row.
  const idRef = useRef(null);
  if (!isEdit && !idRef.current) idRef.current = newTodoId();

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  // The server sends assigneeCanOpen when this save set someone else's assignee or link.
  const afterSave = (res) => {
    if (res?.assigneeCanOpen === false && res.assignee?.id && res.assignee.id !== meId) {
      toast(`Saved. ${res.assignee?.name || 'They'} can't open the linked record, they'll see its name only.`, 'info');
    } else if (!isEdit && teamView && res?.isPrivate) {
      // The Team list never shows a private to-do, so say where it went.
      toast('Added to Mine. Only you can see it.');
    } else {
      toast(isEdit ? 'Saved.' : 'Added.');
    }
    onSaved?.(res);
    onClose();
  };

  const handleSave = async () => {
    setError('');
    const title = f.title.trim();
    if (!title) return setError('Give it a title.');
    if (title.length > TITLE_MAX) return setError(`Keep the title under ${TITLE_MAX} characters.`);
    if (f.notes.length > NOTES_MAX) return setError(`Keep the notes under ${NOTES_MAX} characters.`);
    const dueDate = f.dueDate || null;
    if (dueDate && !validDay(dueDate)) return setError('Pick a real date.');
    if (!isEdit && isAdmin && !f.assigneeId) return setError('Pick who it is for.');
    try {
      if (isEdit) {
        const patch = {};
        if (editable) {
          if (title !== base.title.trim()) patch.title = title;
          if (dueDate !== (base.dueDate || null)) patch.dueDate = dueDate;
          if (linkKey(f.link) !== linkKey(base.link)) patch.link = toBody('link', f.link);
          if (isAdmin && f.assigneeId && f.assigneeId !== base.assigneeId) patch.assigneeId = f.assigneeId;
        }
        if (f.notes !== base.notes) patch.notes = f.notes;
        if (f.money !== base.money) patch.money = f.money;
        if (!Object.keys(patch).length) return onClose();
        // Like done, the $ mark is never checked, so it has no `was`.
        const was = Object.fromEntries(Object.keys(patch).filter((k) => k !== 'money').map((k) => [k, toBody(k, base[k])]));
        // A refusal refetches the to-do (useUpdateTodo), which brings up the choice above.
        const res = await update.mutateAsync({ id: todo.id, ...patch, was });
        afterSave(res);
      } else {
        const body = { id: idRef.current, title, notes: f.notes };
        if (dueDate) body.dueDate = dueDate;
        if (isAdmin && f.assigneeId && f.assigneeId !== meId) body.assigneeId = f.assigneeId;
        if (f.link) body.link = { type: f.link.type, id: f.link.id };
        if (f.money) body.money = true;
        if (initial?.source) {
          body.source = initial.source;
          if (initial.sourceId) body.sourceId = initial.sourceId;
          if (initial.sourceItem) body.sourceItem = initial.sourceItem;
        }
        const res = await create.mutateAsync(body);
        afterSave(res);
      }
    } catch (err) {
      setError(err?.message || 'Could not save it. Try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await del.mutateAsync(todo.id);
      setConfirmDelete(false);
      toast('Deleted.');
      onDeleted?.();
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(err?.message || 'Could not delete it. Try again.');
    }
  };

  let byLine = '';
  if (isEdit && todo.isPrivate) byLine = 'Only you can see this.';
  else if (isEdit && todo.createdBy?.name && todo.createdBy.id !== todo.assignee?.id) {
    byLine = todo.assignee?.id === meId
      ? `From ${todo.createdBy.name}`
      : `From ${todo.createdBy.name} to ${todo.assignee?.name || 'someone'}`;
  }

  return (
    <>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>{isEdit ? (editable ? 'Edit to-do' : 'To-do') : 'New to-do'}</Box>
        <IconButton data-track="todos.dialog.close" onClick={onClose} disabled={busy} aria-label="Close" sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {byLine && <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>{byLine}</Typography>}

          {conflicts.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>This to-do changed while you had it open.</Typography>
              {conflicts.map((k) => (
                <Typography
                  key={k}
                  variant="body2"
                  sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 160, overflowY: 'auto' }}
                >
                  {newValueText(k, latest, todo, today)}
                </Typography>
              ))}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Button data-track="todos.dialog.conflict.useNew" variant="outlined" color="inherit" onClick={() => resolve(true)} sx={btn}>
                  Use the new version
                </Button>
                <Button data-track="todos.dialog.conflict.keepMine" color="inherit" onClick={() => resolve(false)} sx={btn}>
                  Keep mine
                </Button>
              </Box>
            </Alert>
          )}

          {editable ? (
            <TextField
              label="Title"
              value={f.title}
              onChange={set('title')}
              fullWidth
              required
              autoFocus={!isEdit && !isMobile}
              slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
            />
          ) : (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Title</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{todo.title}</Typography>
            </Box>
          )}

          <MoneyMark
            label="Money"
            marked={f.money}
            onToggle={() => setF((prev) => ({ ...prev, money: !prev.money }))}
            trackKey="todos.dialog.money"
            sx={{ mt: -1, ml: -1 }}
          />

          <TextField
            label="Notes"
            value={f.notes}
            onChange={set('notes')}
            multiline
            minRows={3}
            maxRows={12}
            fullWidth
            slotProps={{ htmlInput: { maxLength: NOTES_MAX } }}
          />

          {editable ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Due"
                type="date"
                value={f.dueDate}
                onChange={set('dueDate')}
                sx={{ flex: 1 }}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: '2000-01-01', max: '2100-12-31' } }}
              />
              <Button
                data-track="todos.dialog.clearDate"
                onClick={() => setF((prev) => ({ ...prev, dueDate: '' }))}
                disabled={!f.dueDate}
                sx={btn}
              >
                Clear
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Due</Typography>
              <Typography variant="body2">{todo.dueDate ? fmtDue(todo.dueDate, today) : 'No due date'}</Typography>
            </Box>
          )}

          {isAdmin && editable && (
            <AssigneePicker
              value={f.assigneeId}
              onChange={(id) => setF((prev) => ({ ...prev, assigneeId: id }))}
              fallback={todo?.assignee}
              meId={meId}
              trackKey="todos.dialog.assignee"
            />
          )}

          {editable ? (
            <RecordLinkPicker value={f.link} onChange={(link) => setF((prev) => ({ ...prev, link }))} />
          ) : todo.link ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Link</Typography>
              <TodoLinkChip link={todo.link} trackKey="todos.dialog.link" />
            </Box>
          ) : null}

          {!editable && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Only an admin can change this to-do.</Typography>
          )}
          {error && !conflicts.length && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {isEdit && todo.canDelete && (
          <Button
            data-track="todos.dialog.delete"
            color="error"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            sx={{ ...btn, mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button data-track="todos.dialog.cancel" onClick={onClose} disabled={busy} sx={btn}>Cancel</Button>
        <Button data-track="todos.dialog.save" variant="contained" onClick={handleSave} disabled={busy || conflicts.length > 0} sx={btn}>
          {create.isPending || update.isPending ? 'Saving...' : isEdit ? 'Save' : 'Add'}
        </Button>
      </DialogActions>

      <Dialog open={confirmDelete} onClose={del.isPending ? undefined : () => setConfirmDelete(false)} maxWidth="xs" fullWidth data-track-area="todos">
        <DialogTitle>Delete this to-do?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>It can't be brought back.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button data-track="todos.dialog.delete.keep" onClick={() => setConfirmDelete(false)} disabled={del.isPending} sx={btn}>
            Keep it
          </Button>
          <Button
            data-track="todos.dialog.delete.confirm"
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={del.isPending}
            sx={btn}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
